import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

const OCCURRED_VS_ATTRIBUTED_FIGURE = (
  <Figure
    title="Which date an event is counted in"
    caption="Only usage movements are counted in a different period than the one they occurred in."
  >
    <Flow
      steps={[
        { title: 'Event recorded', text: 'occurredAt is the economically real moment.' },
        {
          fork: [
            {
              title: 'Any other event',
              text: 'attributedAt equals occurredAt.',
              tone: 'muted',
            },
            {
              title: 'Usage movement',
              text: 'attributedAt falls in the service period the usage was consumed in.',
              tone: 'accent',
            },
          ],
        },
        { title: 'Analytics window', text: 'Filters on attributedAt.' },
      ]}
    />
  </Figure>
);

const STEPS: { title: string; body: string; figure?: ReactNode; entity?: string; entityLabel?: string }[] = [
  {
    title: 'One log, fourteen verbs',
    body: 'Every revenue-moving moment writes exactly one event: CREATED, RENEWED, REACTIVATED, EXPANDED, CONTRACTED, CANCEL_SCHEDULED, CHURNED, plus the migration and component events MIGRATE_MODULE, MIGRATE_BUNDLE, MIGRATE_COMPONENT, ADD_COMPONENT and REMOVE_COMPONENT. Subscription status is a convenience view; the event log is the source of truth. If a number in analytics looks wrong, the log is where you check — not the subscription document.',
    entity: 'lifecycleEvent',
    entityLabel: 'Lifecycle Event',
  },
  {
    title: 'Two deltas per event',
    body: 'Each event stamps the MRR delta twice: in the subscription\'s native currency and converted to your reporting currency at that day\'s rate. FX shifts later never rewrite history — a EUR expansion from March keeps March\'s conversion forever, which is what makes month-over-month comparisons stable.',
    entity: 'exchangeRate',
    entityLabel: 'Exchange Rate',
  },
  {
    title: 'Two timestamps per event',
    body: 'occurredAt is the economically real moment (the renewal boundary, the day the expansion took effect); createdAt is when the row was written. Backfills and late corrections make these differ. Analytics windows use a third field, attributedAt, which defaults to occurredAt. It differs only for usage movements, which are booked in the service period the usage was consumed in, not on the invoice date. A correction entered today for a March event still lands in March, where it belongs.',
    figure: OCCURRED_VS_ATTRIBUTED_FIGURE,
  },
  {
    title: 'Query it',
    body: 'subscriptionLifecycleEvents(subscriptionId) returns one subscription\'s timeline, oldest first. organizationLifecycleEvents(organizationId, from?, to?) is the customer-wide view across all their subscriptions, capped at 1000 rows — use the date window to page through long histories.',
  },
  {
    title: 'Corrections are new events',
    body: 'The log is append-only: nothing is ever updated or deleted. A wrong CHURNED gets balanced by a REACTIVATED; a mis-sized expansion gets a compensating CONTRACTED. This is deliberate — an audit trail you can edit is not an audit trail. If a delta looks odd, look for its correcting sibling before assuming a bug.',
    entity: 'lifecycleEvent',
    entityLabel: 'Lifecycle Event',
  },
  {
    title: 'Reconcile analytics against it',
    body: 'MRR movement, ARR, NRR, and churn are all derived by summing event deltas over windows — mrrMovement\'s New/Expansion/Contraction/Churn buckets map one-to-one onto event types. To verify any dashboard figure, sum the reporting-currency deltas for that window yourself; monthlyDrillDown gives you the per-subscription rows behind any single bar.',
  },
];

export function LifecycleLogGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Read the lifecycle event log — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Lifecycle event log</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Read the <em>lifecycle</em> event log.
        </h1>
        <p className="dp-oneliner">
          The append-only record every metric derives from — how to query it, why it stores two
          deltas and two timestamps, and how to reconcile a dashboard number back to its events.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
